/**
 * Authentication Interceptor
 * Attaches auth token to outgoing HTTP requests using legacy-compatible rules.
 * - Main token: only for utilities/local API endpoints and non-IA requests.
 * - IA token: applied only for IA requests when available.
 */

import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { IaAssistService } from '../../features/shared/services/ia-assist.service';
import { catchError, first, switchMap } from 'rxjs/operators';
import { throwError } from 'rxjs';

/** Endpoints that do not require authentication */
// `/recupera-senha` carries the e-mail link hash in the Authorization header: a logged user's token must not replace it
const PUBLIC_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/forgot-password', '/recupera-senha'];

/** Host filters for APIs that require the main auth token. */
const API_HOST_MATCHERS = ['https://utilities', 'http://localhost'];

/**
 * Check whether the request URL targets a public endpoint
 */
function isPublicRequest(url: string): boolean {
  return PUBLIC_ENDPOINTS.some(endpoint => url.includes(endpoint));
}

function isMainApiRequest(url: string): boolean {
  return API_HOST_MATCHERS.some(base => url.startsWith(base));
}

function isIaAssistRequest(url: string): boolean {
  return url.includes('/iaassist');
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (isPublicRequest(req.url)) {
    return next(req);
  }

  const authService = inject(AuthService);
  const token = authService.getToken();
  const iaToken = localStorage.getItem('token_ia');
  const isMainApi = isMainApiRequest(req.url);
  const isIaRequest = isIaAssistRequest(req.url);

  const iaAssistService = inject(IaAssistService);

  if (isIaRequest) {
    // normalize token stored in localStorage (could be JSON or raw string)
    let tokenValue = iaToken ?? undefined;
    const normalize = (val?: string): string | undefined => {
      if (!val) return undefined;
      // quick detect if contains access_token key
      if (val.includes('access_token')) {
        try {
          const p = JSON.parse(val);
          return p?.access_token ?? p?.accessToken ?? undefined;
        } catch {
          // try regex fallback
          const regex = /"access_token"\s*:\s*"([^"]+)"/;
          const m = regex.exec(val);
          if (m && m[1]) return m[1];
        }
      }
      // if seems like a jwt (three parts separated by '.') return as-is
      if (val.split('.').length === 3) return val;
      return val;
    };

    tokenValue = normalize(tokenValue);
    // if we cleaned/normalized the token and storage contained JSON, persist cleaned token
    try {
      const raw = localStorage.getItem('token_ia');
      if (raw && raw.includes('access_token') && tokenValue) {
        localStorage.setItem('token_ia', tokenValue);
      }
    } catch (e) {
      console.warn('Failed to normalize token_ia in storage', e);
    }

    if (tokenValue) {
      const iaRequest = req.clone({
        setHeaders: {
          'Authorization-SeniorX': tokenValue,
          'X-Skip-Auth-Logout': '1'
        },
      });

      return next(iaRequest).pipe(
        catchError((err: any) => {
          // try to refresh IA token only for IA requests on 401
          if (err?.status === 401) {
            return iaAssistService.authenticationIa().pipe(
              first(),
              switchMap((resp: any) => {
                // resp may contain jsonToken (string) or token fields
                let newToken: string | undefined = resp?.jsonToken ?? resp?.token ?? resp?.access_token ?? resp?.accessToken;
                if (typeof newToken === 'string') {
                  newToken = normalize(newToken);
                }

                if (!newToken) {
                  return throwError(() => err);
                }

                try { localStorage.setItem('token_ia', newToken); } catch (e) { console.warn('Failed to persist token_ia', e); }
                const retried = req.clone({ setHeaders: { 'Authorization-SeniorX': newToken, 'X-Skip-Auth-Logout': '1' } });
                return next(retried);
              }),
              catchError(() => throwError(() => err))
            );
          }

          return throwError(() => err);
        })
      );
    }
    // no token available — proceed without IA header (server may return 401 handled elsewhere)
    return next(req);
  }

  if (!token || !isMainApi || isIaRequest) {
    return next(req);
  }

  // Keep token as-is to preserve backend compatibility.
  // Keep token as-is to preserve backend compatibility.
  const authenticatedRequest = req.clone({
    setHeaders: {
      Authorization: token,
    },
  });

  return next(authenticatedRequest);
};
